
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { endpoints } from '../config';
import Dropdown from './Dropdown';
import defaultNetworkIcon from './icon-bento-default-network.svg';
import closeIcon from './close_icon.svg';

// Types
type Network = {
  name: string;
  description: string;
};

/**
 * Lists the networks this user may switch to.
 *
 * The Journeys portal calls `auth/me/networks`. That endpoint HANGS on dev — no response, no
 * error, verified at 35s with a valid token, while the same token gets `v1/godlike/networks` in
 * ~650ms. Because this app is GodLike-only anyway, we use the godlike endpoint, which is also what
 * viana-superadmin-portal uses (src/actions/users.js). It returns `{ id, name }` with no
 * description, so the name doubles as the label.
 */
const fetchNetworks = async (): Promise<Network[]> => {
  try {
    const res = await axios.get(`${endpoints.base_url}v1/godlike/networks`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'X-NETWORK-ID': localStorage.getItem('currentNetwork') || '',
      },
    });
    const list: Array<{ id?: string; name: string; description?: string }> = res.data?.data ?? [];
    return list
      .map((n) => ({ name: n.name, description: n.description || n.name }))
      .sort((a, b) => a.description.localeCompare(b.description));
  } catch (error: unknown) {
    console.log('Error fetching networks:', error);
    // const err = error as AxiosError;
    // if (err.response?.status === 401) {
    //   window.location.href = '/login';
    // }
    // throw error;
    return [];
  }
};

/**
 * Switching is client-side on purpose.
 *
 * The portal POSTs `auth/me/network-switch` to record the choice server-side; that endpoint is on
 * the same `auth/me/*` surface that hangs on dev, and it is not needed here — scope travels on the
 * X-NETWORK-ID header, which the axios interceptors read from localStorage on every request. So we
 * set the value and reload.
 */
const networkSwitch = async (_newNetwork: string): Promise<{ status: boolean }> => {
  return { status: true };
};

const NetworkSwitcher: React.FC = () => {
  const [myNetworks, setMyNetworks] = useState<Network[]>([]);
  const [recentNetworks, setRecentNetworks] = useState<Network[]>([]);
  const [currentNetworkUsing, setCurrentNetworkUsing] = useState<string>('');
  const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false);
  const [showSeeAllModal, setShowSeeAllModal] = useState<boolean>(false);
  const [searchNetworkResult, setSearchNetworkResult] = useState<Network[]>([]);
  const [searchNetwork, setSearchNetwork] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const currentNetworkUsingCheckerRef = React.useRef<string>("");
  const networkSwitcherRef = React.useRef<HTMLButtonElement>(null) as React.RefObject<HTMLElement>;

  // Functions must be declared before useEffect
  const handleLocalStorageChange = () => {
    if (currentNetworkUsingCheckerRef.current === '' || currentNetworkUsingCheckerRef.current == null) {
      currentNetworkUsingCheckerRef.current = localStorage.getItem('currentNetwork') || '';
    }
    if (currentNetworkUsingCheckerRef.current !== localStorage.getItem('currentNetwork')) {
      setShowRefreshModal(true);
    }
  };

  const { data: networksData } = useQuery<Network[]>({
    queryKey: ['networks'],
    queryFn: fetchNetworks,
  });

  useEffect(() => {
    if (Array.isArray(networksData) && networksData.length > 0) {
      const formattedNetworks: Network[] = [];

      let currentNetworkUsingTemp = '';
      networksData.forEach((network: Network) => {
        formattedNetworks.push(network);
        if (network.name === localStorage.getItem('currentNetwork')) {
          currentNetworkUsingTemp = network?.description ? network.description : network.name;
          currentNetworkUsingCheckerRef.current = network.name;
        }
      });

      // Avoid direct setState in effect: batch updates
      setTimeout(() => {
        setMyNetworks(formattedNetworks);
        setCurrentNetworkUsing(currentNetworkUsingTemp);
      }, 0);
    }
  }, [networksData]);

  useEffect(() => {
    window.addEventListener('storage', handleLocalStorageChange);
    return () => window.removeEventListener('storage', handleLocalStorageChange);
  }, []);

  useEffect(() => {
    const recentNetworkStorage = localStorage.getItem('recentNetworks');
    const currentNetworkStorage = localStorage.getItem('currentNetwork');
    let recentNetworkList: Network[] = [];
    if (currentNetworkStorage != null && currentNetworkUsing !== '' && recentNetworkStorage != null) {
      const recentNetworkObject = JSON.parse(recentNetworkStorage);
      recentNetworkList = recentNetworkObject.recentNetworks;
      const doesExist = recentNetworkList.some((network: Network) => network.name === currentNetworkStorage);
      if (!doesExist) {
        recentNetworkList.push({
            name: currentNetworkStorage,
            description: currentNetworkUsing,
        });
        recentNetworkObject.recentNetworks = recentNetworkList;
        localStorage.setItem('recentNetworks', JSON.stringify(recentNetworkObject));
      }
      recentNetworkList.sort(function (a, b) {
        const textA = a.description.toUpperCase();
        const textB = b.description.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      });
      recentNetworkList.sort(function (a, b) {
        return a.name === currentNetworkStorage ? -1 : b.name === currentNetworkStorage ? 1 : 0;
      });
      setTimeout(() => {
        setRecentNetworks(recentNetworkList);
      }, 0);
    }
  }, [currentNetworkUsing]);

  useEffect(() => {
    if (searchNetwork === '' || searchNetwork == null) {
      setTimeout(() => {
        setSearchNetworkResult(myNetworks);
      }, 0);
    } else {
      const result: Network[] = myNetworks.filter((network: Network) => {
        const searchNetworkLowerCase = searchNetwork.toLowerCase();
        const networkLowerCase = network.description.toLowerCase();
        return networkLowerCase.includes(searchNetworkLowerCase);
      });
      setTimeout(() => {
        setSearchNetworkResult(result);
      }, 0);
    }
  }, [myNetworks, searchNetwork]);

  // TanStack mutation for network switch
  const switchNetworkMutation = useMutation({
    mutationFn: networkSwitch,
    onSuccess: (data, network) => {
      if (data.status) {
        console.log('Network switched successfully to', network);
        localStorage.setItem('currentNetwork', network);
        // The interceptor will automatically pick up the new network from localStorage
        // Reload in place: demos hold network-scoped data in memory and in open
        // sockets, and staying on the current route keeps the user where they were.
        window.location.reload();
      }
    },
    onError: (error: unknown) => {
      console.log('Error switching network:', error);
      // const err = error as AxiosError;
      // if (err.response?.status === 401) {
      //   window.location.href = '/login';
      // }
    },
  });

  const handleSwitchNetwork = (network: string) => {
    switchNetworkMutation.mutate(network);
  };


  return (
    <>
      {/* Dropdown */}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen((dropdownOpen) => !dropdownOpen)}
          className={`cursor-pointer flex items-center focus:outline-none min-w-0 p-1.5 px-2 rounded-md hover:bg-gray-200 transition border border-gray-200 leading-7.5 sm:min-w-75 sm:p-2 sm:px-4 ${dropdownOpen ? 'bg-gray-200' : ''}`}
          ref={networkSwitcherRef as React.RefObject<HTMLButtonElement>}
        >
          <span className="me-1.5 shrink-0 sm:me-2">
            <img src={defaultNetworkIcon} width={20} alt="" />
          </span>
          <span className="mr-1.5 min-w-0 truncate text-sm text-gray-700 font-medium sm:mr-2 sm:text-base">
            {currentNetworkUsing || 'Loading Network'}
          </span>

          <span className="flex-grow shrink-0 w-2 sm:w-0" aria-hidden="true" />
          
          <span className="ml-1 shrink-0 sm:ml-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-gray-600 inline-block"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={4}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </button>
        <Dropdown open={dropdownOpen} onClose={() => setDropdownOpen(false)} anchorRef={networkSwitcherRef}>
          <div className="w-75 rounded-md overflow-hidden">

            <div className="text-right p-2 pb-0">
              <small className="text-gray-500">
                {searchNetwork.length <= 0 ? myNetworks.length : searchNetworkResult.length} Total Networks
              </small>
            </div>
            <div className="flex items-center m-2 border border-gray-200">
              <svg className="w-4 h-4 text-gray-400 m-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              <input
                type="text"
                className="w-full border-0 focus:ring-0 focus:outline-none text-sm px-2 py-1"
                placeholder="Search network name"
                value={searchNetwork}
                onChange={e => setSearchNetwork(e.target.value)}
              />
            </div>
            {searchNetwork.length <= 0 && (
              <div className="px-3 py-1 text-xs text-gray-500 bg-gray-100">RECENT NETWORK</div>
            )}
            {(searchNetworkResult.length <= 0 && searchNetwork.length > 0) && (
              <div className="px-3 py-1 text-xs text-gray-500 bg-gray-100">NO AVAILABLE NETWORKS</div>
            )}
            {(searchNetworkResult.length > 0 && searchNetwork.length > 0) && (
              <div className="px-3 py-1 text-xs text-gray-500 bg-gray-100">AVAILABLE NETWORKS</div>
            )}
            <div className="max-h-64 overflow-y-auto">
              <ul className="p-0 m-0 list-none">
                {searchNetwork.length <= 0 && recentNetworks.slice(0, 10).map((network, key) => (
                  <li
                    key={key}
                    className={`px-3 py-3 cursor-pointer text-sm ${network.description === currentNetworkUsing ? 'text-blue-600 bg-blue-50' : 'hover:bg-gray-200'}`}
                    onClick={() => network.description !== currentNetworkUsing && handleSwitchNetwork(network.name)}
                  >
                    {network?.description || network.name}
                  </li>
                ))}
                {searchNetwork.length > 0 && searchNetworkResult.slice(0, 20).map((network, key) => (
                  <li
                    key={key}
                    className="px-3 py-2 cursor-pointer text-sm hover:bg-gray-100"
                    onClick={() => handleSwitchNetwork(network.name)}
                  >
                    {network?.description || network.name}
                  </li>
                ))}
              </ul>
            </div>
            {searchNetworkResult.length > 0 && (
              <div
                onClick={() => { setShowSeeAllModal(true); setDropdownOpen(false); }}
                className="px-3 py-2 cursor-pointer bg-gray-300 border-t border-t-gray-200 text-center text-sm hover:bg-gray-400"
              >
                See All
              </div>
            )}
          </div>
        </Dropdown>
      </div>

      {/* Refresh Modal */}
      {showRefreshModal && (
        <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/80">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <h4 className="mb-3 font-bold text-lg">Refresh Required: Network Update</h4>
            <p className="mb-4">To keep things running smoothly, we need you to refresh the page. Click "Refresh" to apply the latest network.</p>
            <div className="flex justify-end">
              <button
                type="button"
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 cursor-pointer"
                onClick={() => { document.location.href = window.location.origin; }}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* See All Modal */}
      {showSeeAllModal && (
        <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-white rounded shadow-lg w-full max-w-md">
            <div className="flex flex-row justify-between items-center mb-2 p-4 pb-0">
              <p className="font-bold text-lg mb-0">Networks</p>
              <img
                onClick={() => setShowSeeAllModal(false)}
                src={closeIcon}
                width={12}
                height={12}
                className="cursor-pointer"
                alt="Close"
              />
            </div>
            <div className="flex items-center my-4 p-6 bg-gray-50">
              <img src={defaultNetworkIcon} width={20} className="me-2" />
              <span className="text-blue-500 font-medium">{currentNetworkUsing}</span>
              <span className="ml-auto text-gray-400 text-xs">Current Network</span>
            </div>
            <div className="px-6">
              <div className="flex items-center border border-gray-200 rounded-md mb-2">
                <svg className="w-4 h-4 text-gray-400 m-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                  type="text"
                  className="w-full border-0 focus:ring-0 focus:outline-none text-sm px-2 py-1"
                  placeholder="Search network name"
                  value={searchNetwork}
                  onChange={e => setSearchNetwork(e.target.value)}
                />
              </div>
            </div>
            <div className="text-right border-b border-gray-200 pb-1 px-6">
              <small className="text-gray-500">
                {searchNetwork.length <= 0 ? myNetworks.length : searchNetworkResult.length} Total Networks
              </small>
            </div>
            <div className="max-h-96 overflow-y-auto mb-6">
              {searchNetworkResult.map((network, key) => (
                <div className="flex items-center justify-between py-2 border-b border-gray-200 hover:bg-gray-50 p-6" key={key}>
                  <span>{network.description}</span>
                  <button
                    onClick={() => handleSwitchNetwork(network.name)}
                    type="button"
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded cursor-pointer"
                  >
                    Switch
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default NetworkSwitcher;